import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  FileText,
  Globe,
  BookOpen,
  TrendingUp,
  Database,
  Zap,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Brain,
  Star,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  Building,
  Shield,
  Award,
  Target,
  Network,
  Code,
  Settings,
  Eye,
  Circle,
  Cpu
} from 'lucide-react';

interface ResearchPaper {
  id: string;
  title: string;
  authors: string[];
  year: number;
  journal: string;
  citationCount: number;
  relevanceScore: number;
  summary: string;
  keyFindings: string[];
  futureApplications: string[];
  tags: string[];
  impactFactor: number;
  methodology: string;
  limitations: string[];
}

interface TechnicalInsight {
  category: string;
  findings: string[];
  source: string;
  confidence: number;
  businessImpact: string;
}

interface CompetitorAnalysis {
  company: string;
  strengths: string[];
  weaknesses: string[];
  marketShare: string;
  funding: string;
  keyInsight: string;
  threatLevel: 'low' | 'medium' | 'high';
  productFocus: string[];
}

interface DeepResearchPhaseProps {
  companyName: string;
  domain: string;
  onComplete: (results: any) => void;
  initialState?: {
    activeTab?: string;
    selectedPaper?: any;
    selectedCompetitor?: any;
  };
  onStateChange?: (state: any) => void;
  isCompleted?: boolean;
  phaseResults?: any;
}

const DeepResearchPhase: React.FC<DeepResearchPhaseProps> = ({ 
  companyName, 
  domain, 
  onComplete,
  initialState,
  onStateChange,
  isCompleted,
  phaseResults
}) => {
  const [activeTab, setActiveTab] = useState(initialState?.activeTab || 'overview');
  const [isProcessing, setIsProcessing] = useState(false);
  const [researchProgress, setResearchProgress] = useState(0);
  const [selectedPaper, setSelectedPaper] = useState<ResearchPaper | null>(initialState?.selectedPaper || null);
  const [selectedCompetitor, setSelectedCompetitor] = useState<CompetitorAnalysis | null>(initialState?.selectedCompetitor || null);
  const [showResults, setShowResults] = useState(false);
  const [isTabCompleted, setIsTabCompleted] = useState<{[key: string]: boolean}>({});
  
  // Progressive research states
  const [currentResearchStage, setCurrentResearchStage] = useState('');
  const [researchLogs, setResearchLogs] = useState<string[]>([]);
  const [streamingResults, setStreamingResults] = useState<any>({});
  const [confidenceScores, setConfidenceScores] = useState<{[key: string]: number}>({});
  const [researchDepth, setResearchDepth] = useState<'fast' | 'standard' | 'comprehensive'>('standard');

  // If the phase is already completed, show the results immediately
  useEffect(() => {
    if (isCompleted && phaseResults) {
      setShowResults(true);
      setResearchProgress(100);
      setIsTabCompleted({
        overview: true,
        papers: true,
        competitors: true,
        insights: true
      });
    }
  }, [isCompleted, phaseResults]);

  // Save state changes for persistence
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        activeTab,
        selectedPaper,
        selectedCompetitor,
        isTabCompleted
      });
    }
  }, [activeTab, selectedPaper, selectedCompetitor, isTabCompleted, onStateChange]);

  // Mark tab as visited/completed when user views it
  useEffect(() => {
    setIsTabCompleted(prev => ({ ...prev, [activeTab]: true }));
  }, [activeTab]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      
      const tabs = ['overview', 'insights', 'papers', 'competitors', 'emerging'];
      const currentIndex = tabs.indexOf(activeTab);
      
      switch (event.key) {
        case 'ArrowLeft':
          if (currentIndex > 0) {
            setActiveTab(tabs[currentIndex - 1]);
            event.preventDefault();
          }
          break;
        case 'ArrowRight':
          if (currentIndex < tabs.length - 1) {
            setActiveTab(tabs[currentIndex + 1]);
            event.preventDefault();
          }
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          const tabIndex = parseInt(event.key) - 1;
          if (tabIndex >= 0 && tabIndex < tabs.length) {
            setActiveTab(tabs[tabIndex]);
            event.preventDefault();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [activeTab]);

  // Research stages with realistic durations
  const researchStages = [
    { name: 'Literature Search & Paper Discovery', icon: FileText, duration: 4000, progress: 15, confidence: 85 },
    { name: 'Patent Analysis & IP Research', icon: Award, duration: 3500, progress: 30, confidence: 88 },
    { name: 'Industry Report Synthesis', icon: BarChart3, duration: 3000, progress: 45, confidence: 92 },
    { name: 'PHD level Deep Research', icon: Brain, duration: 4500, progress: 60, confidence: 89 },
    { name: 'Competitive Intelligence Gathering', icon: Eye, duration: 3500, progress: 75, confidence: 94 },
    { name: 'Regulatory Document Analysis', icon: Shield, duration: 2500, progress: 85, confidence: 96 },
    { name: 'Emerging Technology Assessment', icon: Zap, duration: 2000, progress: 95, confidence: 87 },
    { name: 'Business Intelligence Synthesis', icon: TrendingUp, duration: 1500, progress: 100, confidence: 91 }
  ];

  // Depth multipliers for research thoroughness
  const depthMultipliers = {
    fast: 0.5,
    standard: 1.0,
    comprehensive: 1.8
  };

  // Start progressive research
  const startProgressiveResearch = async () => {
    setIsProcessing(true);
    setResearchProgress(0);
    setResearchLogs([]);
    setStreamingResults({});
    setConfidenceScores({});
    
    const multiplier = depthMultipliers[researchDepth];
    
    for (let i = 0; i < researchStages.length; i++) {
      const stage = researchStages[i];
      setCurrentResearchStage(stage.name);
      
      // Add research log
      setResearchLogs(prev => [...prev, `🔍 ${stage.name}...`]);
      
      // Simulate progressive results streaming
      await new Promise(resolve => {
        let stageProgress = i > 0 ? researchStages[i-1].progress : 0;
        const increment = (stage.progress - stageProgress) / 10;
        const adjustedDuration = stage.duration * multiplier;
        
        const updateProgress = () => {
          stageProgress += increment;
          setResearchProgress(Math.min(stageProgress, stage.progress));
          
          // Stream partial results
          if (stageProgress >= stage.progress * 0.5) {
            setStreamingResults((prev: any) => ({
              ...prev,
              [stage.name]: getPartialResults(stage.name, stageProgress / stage.progress)
            }));
          }
          
          // Update confidence score
          setConfidenceScores(prev => ({
            ...prev,
            [stage.name]: Math.min(stage.confidence + (stageProgress / stage.progress) * 5, 98)
          }));
          
          if (stageProgress >= stage.progress) {
            setResearchLogs(prev => [...prev, `✅ ${stage.name} completed`]);
            resolve(undefined);
          } else {
            setTimeout(updateProgress, adjustedDuration / 10);
          }
        };
        updateProgress();
      });
    }
    
    setIsProcessing(false);
    setShowResults(true);
  };

  // Generate partial results for streaming
  const getPartialResults = (stageName: string, completion: number) => {
    // This would normally stream real research data
    switch (stageName) {
      case 'Literature Search & Paper Discovery':
        return {
          papersFound: Math.round(89 * completion),
          relevantPapers: Math.round(23 * completion),
          keyInsights: Math.round(7 * completion)
        };
      case 'Competitive Intelligence Gathering':
        return {
          competitorsAnalyzed: Math.round(25 * completion),
          marketSegments: Math.round(8 * completion),
          threatAssessments: Math.round(15 * completion)
        };
      default:
        return { progress: `${Math.round(completion * 100)}%` };
    }
  };

  // Template research data
  const researchResultsTemplate = {
    overview: {
      totalSources: 347,
      technicalPapers: 89,
      industryReports: 52,
      patentAnalysis: 67,
      regulatoryDocuments: 41,
      competitorAnalysis: 25,
      duration: '72 hours of analysis',
      confidence: 96
    },
    
    technicalInsights: [
      {
        category: 'ML/AI Breakthrough Technologies',
        findings: [
          'BERT-based models with custom PII tokenization achieve 99.7% accuracy on structured data vs 94.2% industry average',
          'Multi-modal fusion (text + metadata + behavioral context) improves detection by 23% over single-modal approaches',
          'Transformer attention mechanisms excel at understanding PII context and relationships with 15% fewer false positives',
          'Few-shot learning enables rapid adaptation to new PII types with <100 examples (vs 10,000+ traditional methods)',
          'Adversarial training reduces false positives by 34% in noisy data environments',
          'Graph neural networks show 18% improvement in detecting related PII entities across documents',
          'Federated learning maintains 94% accuracy while preserving data locality for global compliance'
        ],
        source: '23 IEEE papers, 12 ACL proceedings, 8 NeurIPS papers',
        confidence: 94,
        businessImpact: 'Core technology differentiator with 2-3 year competitive advantage'
      },
      {
        category: 'Performance & Scalability Engineering',
        findings: [
          'Edge computing deployment reduces global latency by 67% (avg 45ms vs 134ms cloud-only)',
          'Distributed processing architectures handle 10M+ records/second with linear scaling up to 100 nodes',
          'In-memory caching of model weights reduces inference time by 78% with 3GB memory footprint',
          'Batch processing optimizations achieve 15x throughput improvement for large datasets (>1TB)',
          'GPU acceleration provides 240x speedup over CPU-only implementations for transformer models',
          'Streaming architectures maintain <50ms end-to-end latency for real-time PII detection',
          'Auto-scaling based on data velocity reduces infrastructure costs by 45% during low-traffic periods'
        ],
        source: '18 performance studies, 31 benchmark reports, 12 cloud architecture papers',
        confidence: 92,
        businessImpact: 'Enables global deployment with superior cost-performance ratio'
      },
      {
        category: 'Privacy-Preserving Technologies',
        findings: [
          'Homomorphic encryption enables PII detection on encrypted data with only 12% accuracy trade-off',
          'Differential privacy with ε=0.1 maintains 94% utility while ensuring mathematical privacy guarantees',
          'Secure multi-party computation allows cross-organization PII analysis without data sharing',
          'Zero-knowledge proofs enable compliance verification without revealing sensitive data details',
          'Confidential computing (Intel SGX, AMD SEV) provides hardware-level protection for PII processing',
          'Synthetic data generation preserves statistical properties while eliminating privacy risks',
          'Privacy budgeting frameworks optimize utility vs privacy trade-offs automatically'
        ],
        source: '22 cryptography papers, 15 privacy research studies, 9 IACR publications',
        confidence: 88,
        businessImpact: 'Future-proof architecture enabling global enterprise adoption'
      },
      {
        category: 'Regulatory Compliance Innovation',
        findings: [
          'GDPR Article 25 privacy-by-design requirements drive automated compliance checking adoption',
          'CCPA amendments expand PII definition to include biometric and geolocation data (40% scope increase)',
          'India\'s DPDP Act 2023 introduces cross-border data transfer restrictions affecting 67% of global architectures',
          'HIPAA Technical Safeguards mandate comprehensive audit trails for all PII access and processing',
          'Emerging regulations in Brazil (LGPD), Singapore (PDPA), and EU (AI Act) require real-time consent management',
          'Automated compliance reporting reduces manual legal review time by 85% and error rate by 92%',
          'Multi-jurisdiction compliance frameworks handle 15+ regulatory regimes simultaneously'
        ],
        source: '41 regulatory documents, 28 legal analyses, 19 compliance frameworks',
        confidence: 96,
        businessImpact: 'Critical for global market expansion and enterprise sales cycles'
      },
      {
        category: 'Market Dynamics & Business Intelligence',
        findings: [
          'Data privacy market growing at 25.1% CAGR, reaching $25.7B by 2027 (IDC, Gartner consensus)',
          'Enterprise demand for real-time PII detection increased 340% in past 18 months post-GDPR enforcement',
          '67% of organizations report compliance as top driver, 34% cite data breach prevention as primary motivation',
          'Cloud-native solutions preferred by 78% of enterprises over on-premise deployments',
          'Integration complexity is critical success factor for 89% of buyers - API-first architecture essential',
          'Average deal size for enterprise PII solutions: $2.3M ARR with 18-month sales cycles',
          'Customer acquisition cost 3.2x lower for solutions with comprehensive compliance automation'
        ],
        source: '25 market research reports, 52 industry surveys, 34 vendor analyses',
        confidence: 91,
        businessImpact: 'Market timing and positioning optimal for aggressive growth strategy'
      }
    ] as TechnicalInsight[],

    researchPapers: [
      {
        id: 'paper-1',
        title: 'Privacy-Preserving Data Mining Techniques for Personal Information Detection in Unstructured Enterprise Data',
        authors: ['Dr. Sarah Chen (Stanford)', 'Prof. Michael Rodriguez (MIT)', 'Dr. Aisha Patel (CMU)'],
        year: 2024,
        journal: 'IEEE Transactions on Information Forensics and Security',
        citationCount: 312,
        relevanceScore: 9.7,
        impactFactor: 6.234,
        methodology: 'Experimental analysis with 50TB enterprise dataset from 12 Fortune 500 companies',
        summary: 'Comprehensive analysis of machine learning approaches for automated PII detection in unstructured data streams, introducing novel transformer architectures optimized for privacy-sensitive contexts.',
        keyFindings: [
          'Custom BERT tokenization for PII context achieves 99.7% F1-score vs 94.2% baseline models',
          'Multi-head attention mechanisms reduce context-dependent false positives by 34%',
          'Real-time processing at 10M documents/hour feasible with distributed transformer architecture',
          'Cross-lingual PII detection maintains 92% accuracy across 15 languages',
          'Transfer learning reduces training data requirements by 87% for new PII categories'
        ],
        futureApplications: [
          'Multi-modal PII detection (text, images, audio, video) with unified transformer architecture',
          'Cross-organizational federated learning for privacy-preserving model improvement',
          'Real-time PII risk scoring for dynamic data access control',
          'Automated compliance documentation generation for regulatory audits'
        ],
        limitations: [
          'Performance degrades on highly domain-specific jargon',
          'Requires 32GB+ memory for optimal transformer model deployment',
          'Limited evaluation on non-English languages with complex scripts'
        ],
        tags: ['PII Detection', 'BERT', 'Privacy', 'Real-time Processing', 'Enterprise']
      },
      {
        id: 'paper-2',
        title: 'Scalable Architecture for Real-time Compliance Monitoring in Multi-Cloud Enterprise Environments',
        authors: ['Dr. James Wilson (Google)', 'Dr. Lisa Zhang (Amazon)', 'Prof. Robert Kim (Berkeley)'],
        year: 2023,
        journal: 'ACM Transactions on Computer Systems',
        citationCount: 198,
        relevanceScore: 9.4,
        impactFactor: 4.892,
        methodology: 'Production deployment analysis across 8 cloud providers with 2+ years operational data',
        summary: 'Novel architectural patterns for handling high-volume data processing while maintaining regulatory compliance across multiple cloud environments and jurisdictions.',
        keyFindings: [
          'Event-driven microservices architecture enables 10x scalability improvement vs monolithic systems',
          'Edge computing deployment reduces cross-region latency by 67% for global compliance',
          'Automated compliance reporting reduces manual effort by 85% and improves accuracy by 92%',
          'Multi-cloud deployment strategy increases availability to 99.99% with 45% cost optimization',
          'Kubernetes-based auto-scaling handles 100x traffic spikes without service degradation'
        ],
        futureApplications: [
          'Serverless compliance monitoring with sub-millisecond response times',
          'AI-powered compliance prediction preventing violations before they occur',
          'Blockchain-based immutable audit trails for regulatory reporting',
          'Quantum-safe encryption for future-proof data protection architectures'
        ],
        limitations: [
          'Complex setup requiring specialized DevOps expertise',
          'Higher networking costs in multi-cloud scenarios',
          'Vendor lock-in risks with cloud-specific services'
        ],
        tags: ['Cloud Architecture', 'Compliance', 'Scalability', 'Event Processing', 'Microservices']
      },
      {
        id: 'paper-3',
        title: 'Adaptive Neural Networks for Dynamic Privacy Classification Under Evolving Regulatory Frameworks',
        authors: ['Dr. Emily Thompson (Oxford)', 'Dr. David Lee (Toronto)', 'Prof. Maria Garcia (ETH Zurich)'],
        year: 2024,
        journal: 'Nature Machine Intelligence',
        citationCount: 156,
        relevanceScore: 9.6,
        impactFactor: 25.898,
        methodology: 'Longitudinal study tracking regulatory changes across 25 jurisdictions over 5 years',
        summary: 'Framework for adaptive privacy classification systems that automatically evolve with changing regulatory landscapes using meta-learning and continual learning techniques.',
        keyFindings: [
          'Meta-learning enables 23% accuracy improvement when adapting to new regulatory requirements',
          'Continual learning prevents catastrophic forgetting while incorporating new compliance rules',
          'Multi-jurisdiction compliance achievable with unified framework handling 15+ regulatory regimes',
          'Transfer learning reduces adaptation time by 78% for new geographical markets',
          'Automated policy synthesis generates human-readable compliance documentation'
        ],
        futureApplications: [
          'Self-evolving privacy policies that adapt to regulatory changes automatically',
          'Predictive compliance modeling anticipating regulatory shifts 6-12 months ahead',
          'Automated compliance workflow generation for complex multi-national operations',
          'Real-time regulatory impact assessment for business process changes'
        ],
        limitations: [
          'Requires continuous monitoring of regulatory changes',
          'High computational overhead for meta-learning algorithms',
          'Bias towards well-documented regulatory frameworks'
        ],
        tags: ['Adaptive Systems', 'Regulatory Compliance', 'Meta-Learning', 'Continual Learning']
      },
      {
        id: 'paper-4',
        title: 'Homomorphic Encryption for Privacy-Preserving Machine Learning in Production Enterprise Systems',
        authors: ['Dr. Alex Chen (IBM Research)', 'Prof. Raj Patel (IIT Mumbai)', 'Dr. Sophie Mueller (TU Munich)'],
        year: 2023,
        journal: 'Communications of the ACM',
        citationCount: 267,
        relevanceScore: 8.9,
        impactFactor: 5.235,
        methodology: 'Implementation study across 15 enterprise deployments with 500TB+ encrypted datasets',
        summary: 'Practical implementation of homomorphic encryption for machine learning workloads in production enterprise environments, addressing performance and scalability challenges.',
        keyFindings: [
          'CKKS scheme optimized for ML achieves 15x performance improvement over baseline implementations',
          'Bootstrapping optimization reduces ciphertext expansion by 60% for deep neural networks',
          'Practical deployment handles 1M+ encrypted records with <2 second inference latency',
          'Security analysis confirms IND-CPA security against quantum adversaries with 128-bit security level',
          'Integration with TensorFlow and PyTorch through custom operators'
        ],
        futureApplications: [
          'Fully homomorphic PII analysis across organizational boundaries',
          'Privacy-preserving federated learning for sensitive datasets',
          'Encrypted database queries maintaining cryptographic privacy guarantees',
          'Secure multi-party computation for competitive intelligence'
        ],
        limitations: [
          'High computational overhead (100-1000x vs plaintext)',
          'Limited to specific ML architectures and operations',
          'Complex key management for enterprise deployments'
        ],
        tags: ['Homomorphic Encryption', 'Privacy-Preserving ML', 'Cryptography', 'Enterprise Security']
      }
    ] as ResearchPaper[],

    competitorAnalysis: [
      {
        company: 'Privacera',
        strengths: [
          'Strong Hadoop/Spark ecosystem integration',
          'Established enterprise customer base',
          'Comprehensive data governance platform',
          'Deep expertise in Apache Ranger and Atlas'
        ],
        weaknesses: [
          'Limited real-time processing capabilities (<1000 req/sec)',
          'Complex deployment model requiring 3-6 months implementation',
          'Primarily batch-oriented architecture',
          'Weak multi-cloud support'
        ],
        marketShare: '18%',
        funding: '$50M Series B (Accel, Norwest)',
        keyInsight: 'Focus on legacy big data infrastructure leaves real-time and cloud-native gaps',
        threatLevel: 'medium',
        productFocus: ['Data Discovery', 'Access Control', 'Data Masking', 'Compliance Reporting']
      },
      {
        company: 'Immuta',
        strengths: [
          'Advanced dynamic policy engine with 200+ built-in policies',
          'Strong data science workflow integration',
          'Excellent data governance and lineage tracking',
          'Good cloud platform integrations'
        ],
        weaknesses: [
          'High cost of ownership ($500K+ annual contracts)',
          'Limited automated PII discovery capabilities',
          'Complex configuration requiring specialized training',
          'Performance issues with high-volume real-time data'
        ],
        marketShare: '22%',
        funding: '$90M Series C (Spark Capital, DFJ)',
        keyInsight: 'Policy-first approach but lacks intelligent automated PII discovery and classification',
        threatLevel: 'high',
        productFocus: ['Policy Management', 'Data Access Control', 'Privacy Engineering', 'Data Science']
      },
      {
        company: 'BigID',
        strengths: [
          'Comprehensive data discovery across 100+ data sources',
          'Strong multi-cloud and hybrid deployment support',
          'Advanced data classification with ML models',
          'Good regulatory compliance reporting'
        ],
        weaknesses: [
          'Resource intensive (requires 64GB+ RAM for large deployments)',
          'Complex pricing model with per-record costs',
          'Limited real-time protection capabilities',
          'Steep learning curve for non-technical users'
        ],
        marketShare: '25%',
        funding: '$414M total (Tiger Global, Bessemer)',
        keyInsight: 'Discovery-focused platform but limited real-time protection and lacks API-first architecture',
        threatLevel: 'high',
        productFocus: ['Data Discovery', 'Privacy Management', 'Compliance Automation', 'Risk Assessment']
      },
      {
        company: 'Varonis',
        strengths: [
          'Deep file system and directory service integration',
          'Strong threat detection and insider threat analytics',
          'Established presence in financial services and healthcare',
          'Good Windows Active Directory integration'
        ],
        weaknesses: [
          'Windows-centric architecture limiting cloud adoption',
          'Limited modern cloud-native features',
          'Complex on-premise deployment model',
          'Weak API ecosystem for integration'
        ],
        marketShare: '15%',
        funding: 'Public (NASDAQ: VRNS, $4.2B market cap)',
        keyInsight: 'Security-first approach with legacy architecture incompatible with modern cloud-native requirements',
        threatLevel: 'low',
        productFocus: ['Data Security', 'Threat Detection', 'User Analytics', 'File System Monitoring']
      },
      {
        company: 'OneTrust',
        strengths: [
          'Comprehensive privacy management platform',
          'Strong regulatory compliance coverage (GDPR, CCPA, etc.)',
          'Good integration with legal and compliance workflows',
          'Extensive third-party risk management'
        ],
        weaknesses: [
          'Limited technical data discovery capabilities',
          'Primarily manual and workflow-driven vs automated',
          'Expensive enterprise-only pricing model',
          'Weak real-time data processing features'
        ],
        marketShare: '12%',
        funding: '$300M Series C (Insight Partners)',
        keyInsight: 'Compliance-focused solution lacking technical depth for developer and engineering teams',
        threatLevel: 'medium',
        productFocus: ['Privacy Management', 'Compliance Workflows', 'Risk Assessment', 'Legal Operations']
      }
    ] as CompetitorAnalysis[],

    emergingTechnologies: [
      {
        technology: 'Quantum-Safe Cryptography',
        maturity: 'Research/Early Development',
        timeToMarket: '3-5 years',
        impact: 'Future-proofs PII protection against quantum computing threats',
        keyPlayers: ['IBM Quantum', 'Google Quantum', 'NIST Post-Quantum Cryptography'],
        skyflowOpportunity: 'Early adoption positioning for quantum-ready PII encryption and compliance',
        investmentRequired: 'High ($5-10M)',
        riskLevel: 'Medium'
      },
      {
        technology: 'Confidential Computing',
        maturity: 'Early Adoption',
        timeToMarket: '1-2 years',
        impact: 'Enables secure PII processing in untrusted cloud environments',
        keyPlayers: ['Intel SGX', 'AMD SEV', 'ARM TrustZone', 'Azure Confidential Computing'],
        skyflowOpportunity: 'Secure multi-tenant PII processing with hardware-level isolation',
        investmentRequired: 'Medium ($2-5M)',
        riskLevel: 'Low'
      },
      {
        technology: 'Synthetic Data Generation',
        maturity: 'Growing/Mainstream',
        timeToMarket: '6-12 months',
        impact: 'Creates realistic test data while preserving privacy and compliance',
        keyPlayers: ['Mostly AI', 'Syntegra Health', 'Gretel.ai', 'DataSynthesizer'],
        skyflowOpportunity: 'Privacy-preserving development, testing, and analytics workflows',
        investmentRequired: 'Low ($500K-2M)',
        riskLevel: 'Low'
      },
      {
        technology: 'Federated Learning for Privacy',
        maturity: 'Early Adoption',
        timeToMarket: '1-2 years',
        impact: 'Enables collaborative ML training without centralized data sharing',
        keyPlayers: ['Google Federated Learning', 'Microsoft FL', 'NVIDIA FLARE'],
        skyflowOpportunity: 'Cross-organizational PII model training while maintaining data locality',
        investmentRequired: 'Medium ($3-7M)',
        riskLevel: 'Medium'
      }
    ]
  };

  const completeResearch = () => {
    onComplete({
      researchSummary: researchResultsTemplate.overview,
      technicalInsights: researchResultsTemplate.technicalInsights,
      papers: researchResultsTemplate.researchPapers,
      competitors: researchResultsTemplate.competitorAnalysis,
      emergingTech: researchResultsTemplate.emergingTechnologies,
      streamingResults,
      confidenceScores,
      businessIntelligence: {
        marketSize: '$25.7B by 2027',
        growthRate: '25.1% CAGR',
        keyDrivers: ['Regulatory Compliance', 'Data Breach Prevention', 'Cloud Migration'],
        marketGaps: [
          'Real-time processing: Only 23% of solutions offer sub-second response',
          'API-first architecture: 67% lack modern integration capabilities',
          'Multi-cloud support: 45% limited to single cloud provider'
        ],
        skyflowPositioning: {
          competitiveAdvantage: [
            '10x faster real-time processing than market leaders',
            'API-first architecture for cloud-native integration',
            'Multi-modal AI for 99.7% detection accuracy',
            'Quantum-ready encryption for future-proofing'
          ],
          targetSegments: [
            'Cloud-native enterprises ($100B+ TAM)',
            'Financial services (strict compliance requirements)',
            'Healthcare (HIPAA + emerging regulations)',
            'Global enterprises (multi-jurisdiction compliance)'
          ],
          pricingStrategy: 'Value-based pricing 30-40% premium justified by superior performance',
          gtmStrategy: 'Developer-first adoption leading to enterprise procurement'
        }
      }
    });
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 95) return 'text-emerald-400';
    if (confidence >= 90) return 'text-blue-400';
    if (confidence >= 85) return 'text-yellow-400';
    return 'text-orange-400';
  };

  const getThreatLevelColor = (level: string) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(99,102,241,0.1),transparent)] opacity-30"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.1),transparent)] opacity-30"></div>
      </div>

      <div className="relative z-20 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 backdrop-blur-xl border border-slate-200/50">
                  <Search className="w-10 h-10 text-blue-600" />
                </div>
                <div>
                  <h1 className="sqsft-heading-1">
                    Deep Research & Knowledge Extraction
                  </h1>
                  <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                    AI-powered research with PhD-level technical analysis for {companyName}
                  </p>
                </div>
              </div>
              
              {!isProcessing && !showResults && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={startProgressiveResearch}
                  className="sqsft-btn-primary"
                >
                  <Search className="w-5 h-5" />
                  <span>Start Research</span>
                </motion.button>
              )}
              
              {showResults && (
                <div className="flex items-center space-x-4">
                  {/* Progress indicator */}
                  <div className="sqsft-caption">
                    {Object.keys(isTabCompleted).length} of 5 sections explored
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={completeResearch}
                    className="sqsft-btn-primary"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>Complete Research</span>
                  </motion.button>
                </div>
              )}
            </div>
          </motion.div>

          {isProcessing && (
              <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6 mb-8"
            >
              {/* Current Stage */}
              <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="sqsft-heading-3">{currentResearchStage}</h3>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="font-bold text-lg" style={{ color: 'rgb(var(--color-deep-green))' }}>{Math.round(researchProgress)}%</div>
                    </div>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                  <motion.div
                    className="h-3 rounded-full"
                    style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}
                    initial={{ width: 0 }}
                    animate={{ width: `${researchProgress}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
              
              {/* Research Logs */}
              <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50 max-h-64 overflow-y-auto" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
                <h4 className="sqsft-heading-4 mb-4">Research Progress</h4>
                <div className="space-y-2">
                  {researchLogs.map((log, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="text-sm sqsft-body flex items-center space-x-2"
                    >
                      {log.startsWith('✅') ? (
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <Search className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      )}
                      <span>{log}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* Streaming Results */}
              {Object.keys(streamingResults).length > 0 && (
                <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
                  <h4 className="sqsft-heading-4 mb-4">Live Results Stream</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(streamingResults).map(([stage, data]: [string, any]) => (
                      <div key={stage} className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-700 mb-2">{stage}</div>
                        <div className="space-y-1">
                          {Object.entries(data).map(([key, value]: [string, any]) => (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="text-gray-600">{key.replace(/([A-Z])/g, ' $1')}</span>
                              <span className="font-medium">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Stage Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {researchStages.map((stage, index) => {
                  const isCompleted = researchProgress >= stage.progress;
                  const isCurrent = currentResearchStage === stage.name;
                  const confidence = confidenceScores[stage.name] || stage.confidence;
                  
                  return (
                    <div
                      key={stage.name}
                      className={`p-3 rounded-xl border transition-all duration-300 ${
                        isCompleted ? 'bg-green-50 border-green-200' :
                        isCurrent ? 'bg-blue-50 border-blue-200' :
                        'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-center mb-2">
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : isCurrent ? (
                          <Search className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className="text-xs text-center font-medium mb-1">{stage.name}</div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {!isProcessing && !showResults && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                <div className="sqsft-feature-card p-6">
                    <h3 className="sqsft-heading-3 mb-4">
                        <span className="sqsft-gradient-text">Live Research Process</span>
                    </h3>
                    <div className="rounded-xl p-4 h-96 overflow-y-auto font-mono text-sm" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="w-16 h-16 sqsft-gradient-primary rounded-2xl flex items-center justify-center mb-4">
                                <Cpu className="w-8 h-8 text-white" />
                            </div>
                            <h4 className="sqsft-heading-4 mb-2">Research Engine</h4>
                            <p className="sqsft-body max-w-xs" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                                The live research process will be shown here once initiated. Click "Start Research" to begin.
                            </p>
                        </div>
                    </div>
                </div>
                 <div className="sqsft-feature-card p-6">
                    <h3 className="sqsft-heading-3 mb-4">
                        <span className="sqsft-gradient-text">Research Framework</span>
                    </h3>
                    <div className="h-96 overflow-y-auto pr-2">
                        <div>
                            <h4 className="sqsft-heading-4 mb-3">Core Research Stages</h4>
                            <div className="space-y-3">
                                {researchStages.map((stage, index) => (
                                    <motion.div
                                        key={stage.name}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="sqsft-feature-card p-4 border hover:border-sage-green transition-all duration-300"
                                    >
                                        <div className="flex items-center space-x-4">
                                            <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                                                <stage.icon className="w-5 h-5 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <h5 className="sqsft-heading-4">{stage.name}</h5>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          )}


          {/* Research Results */}
          {showResults && (
            <>
              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                {[
                  { label: 'Total Sources', value: researchResultsTemplate.overview.totalSources, icon: Database },
                  { label: 'Technical Papers', value: researchResultsTemplate.overview.technicalPapers, icon: FileText },
                  { label: 'Industry Reports', value: researchResultsTemplate.overview.industryReports, icon: BarChart3 },
                  { label: 'Patents', value: researchResultsTemplate.overview.patentAnalysis, icon: Award },
                  { label: 'Regulatory Docs', value: researchResultsTemplate.overview.regulatoryDocuments, icon: Shield },
                  { label: 'Competitors', value: researchResultsTemplate.overview.competitorAnalysis, icon: Building }
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/80 backdrop-blur-xl rounded-xl border border-slate-200/50 p-4 text-center shadow-lg"
                  >
                    <div className="flex justify-center mb-2">
                      <stat.icon className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-gray-600 text-sm">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Enhanced Navigation Tabs with Progress */}
              <div className="mb-8">
                <div className="flex space-x-2 sqsft-card p-2 rounded-2xl backdrop-blur-xl border border-slate-200/50 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
                  {[
                    { id: 'overview', label: 'Research Overview', icon: Globe },
                    { id: 'insights', label: 'Technical Insights', icon: Brain },
                    { id: 'papers', label: 'Research Papers', icon: BookOpen },
                    { id: 'competitors', label: 'Competitive Analysis', icon: Building },
                    { id: 'emerging', label: 'Emerging Tech', icon: Zap }
                  ].map((tab) => {
                    const isCompleted = isTabCompleted[tab.id];
                    const isCurrent = activeTab === tab.id;
                    
                    return (
                      <motion.button
                        key={tab.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setActiveTab(tab.id)}
                        className={`relative flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                          isCurrent
                            ? 'text-white'
                            : 'text-gray-700 hover:bg-gray-100/50'
                        }`}
                        style={isCurrent ? { backgroundColor: 'rgb(var(--color-deep-green))' } : {}}
                      >
                        {isCompleted && !isCurrent && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full" />
                        )}
                        <tab.icon className="w-5 h-5" />
                        <span className="hidden sm:block">{tab.label}</span>
                        <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                      </motion.button>
                    );
                  })}
                </div>
                
                {/* Tab Progress and Navigation */}
                <div className="mt-3 px-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="sqsft-caption">Research Progress:</span>
                      <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${(Object.keys(isTabCompleted).length / 5) * 100}%` }}
                        />
                      </div>
                      <span className="sqsft-caption">
                        {Object.keys(isTabCompleted).length}/5
                      </span>
                    </div>
                    
                    <div className="hidden md:flex items-center space-x-4">
                      {/* Quick Navigation Arrows */}
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => {
                            const tabs = ['overview', 'insights', 'papers', 'competitors', 'emerging'];
                            const currentIndex = tabs.indexOf(activeTab);
                            if (currentIndex > 0) setActiveTab(tabs[currentIndex - 1]);
                          }}
                          disabled={activeTab === 'overview'}
                          className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            const tabs = ['overview', 'insights', 'papers', 'competitors', 'emerging'];
                            const currentIndex = tabs.indexOf(activeTab);
                            if (currentIndex < tabs.length - 1) setActiveTab(tabs[currentIndex + 1]);
                          }}
                          disabled={activeTab === 'emerging'}
                          className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="sqsft-caption">
                        Use ← → or 1-5 keys to navigate
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Sections */}
              <AnimatePresence mode="wait">
                {activeTab === 'overview' && (
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-6"
                  >
                    <div className="sqsft-card backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
                      <h3 className="sqsft-heading-2 mb-6">Research Summary</h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                          <h4 className="sqsft-heading-3 mb-4">Scope & Methodology</h4>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Analysis Duration</span>
                              <span className="text-gray-900 font-medium">{researchResultsTemplate.overview.duration}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Confidence Level</span>
                              <span className={`font-bold ${getConfidenceColor(researchResultsTemplate.overview.confidence)}`}>
                                {researchResultsTemplate.overview.confidence}%
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Focus Domain</span>
                              <span className="text-gray-900 font-medium">{domain}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Findings</h4>
                          <div className="space-y-2">
                            {[
                              'Market opportunity: $25.7B by 2027 (25.1% CAGR)',
                              'Technology gap: Real-time processing leadership opportunity',
                              'Competitive positioning: API-first architecture advantage',
                              'Regulatory drivers: Multi-jurisdiction compliance demand'
                            ].map((finding, i) => (
                              <div key={i} className="flex items-start space-x-2">
                                <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700">{finding}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === 'insights' && (
                  <motion.div
                    key="insights"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-6"
                  >
                    {researchResultsTemplate.technicalInsights.map((insight, index) => (
                      <motion.div
                        key={insight.category}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-6 shadow-lg"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-gray-900">{insight.category}</h3>
                          <div className="flex items-center space-x-4">
                            <span className={`font-bold ${getConfidenceColor(insight.confidence)}`}>
                              {insight.confidence}% confidence
                            </span>
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                              {insight.findings.length} findings
                            </span>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="text-gray-600 text-sm mb-2">Business Impact</div>
                          <div className="text-emerald-600 font-medium">{insight.businessImpact}</div>
                        </div>
                        
                        <div className="space-y-3">
                          {insight.findings.map((finding, i) => (
                            <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                              <Star className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{finding}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                          <div className="text-gray-600 text-sm">Sources: {insight.source}</div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {activeTab === 'papers' && (
                  <motion.div
                    key="papers"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-6"
                  >
                    {researchResultsTemplate.researchPapers.map((paper, index) => (
                      <motion.div
                        key={paper.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => setSelectedPaper(paper)}
                        className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-6 cursor-pointer hover:border-blue-500/50 transition-all duration-300 shadow-lg"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{paper.title}</h3>
                            <p className="text-gray-600 mb-2">
                              {paper.authors.join(', ')} • {paper.journal} ({paper.year})
                            </p>
                          </div>
                          <div className="flex items-center space-x-4 text-sm">
                            <div className="text-center">
                              <div className="text-emerald-600 font-bold">{paper.relevanceScore}</div>
                              <div className="text-gray-500">Relevance</div>
                            </div>
                            <div className="text-center">
                              <div className="text-blue-600 font-bold">{paper.citationCount}</div>
                              <div className="text-gray-500">Citations</div>
                            </div>
                            <div className="text-center">
                              <div className="text-purple-600 font-bold">{paper.impactFactor}</div>
                              <div className="text-gray-500">Impact</div>
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-gray-700 mb-4">{paper.summary}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex flex-wrap gap-2">
                            {paper.tags.map((tag, i) => (
                              <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {activeTab === 'competitors' && (
                  <motion.div
                    key="competitors"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-6"
                  >
                    {researchResultsTemplate.competitorAnalysis.map((competitor, index) => (
                      <motion.div
                        key={competitor.company}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => setSelectedCompetitor(competitor)}
                        className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-6 cursor-pointer hover:border-orange-500/50 transition-all duration-300 shadow-lg"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">{competitor.company}</h3>
                            <p className="text-gray-600">{competitor.keyInsight}</p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getThreatLevelColor(competitor.threatLevel)}`}>
                              {competitor.threatLevel} threat
                            </span>
                            <span className="text-orange-600 font-bold">{competitor.marketShare}</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                          <div>
                            <h4 className="text-gray-900 font-semibold mb-2">Strengths</h4>
                            <div className="space-y-1">
                              {competitor.strengths.map((strength, i) => (
                                <div key={i} className="flex items-start space-x-2">
                                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-700 text-sm">{strength}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-gray-900 font-semibold mb-2">Weaknesses</h4>
                            <div className="space-y-1">
                              {competitor.weaknesses.map((weakness, i) => (
                                <div key={i} className="flex items-start space-x-2">
                                  <Target className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-700 text-sm">{weakness}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-gray-600 text-sm">
                            Funding: {competitor.funding}
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {activeTab === 'emerging' && (
                  <motion.div
                    key="emerging"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-6"
                  >
                    {researchResultsTemplate.emergingTechnologies.map((tech, index) => (
                      <motion.div
                        key={tech.technology}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-6 shadow-lg"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">{tech.technology}</h3>
                            <p className="text-gray-600">{tech.impact}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-cyan-600 font-bold">{tech.timeToMarket}</div>
                            <div className="text-gray-500 text-sm">Time to Market</div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <div className="text-gray-600 text-sm">Maturity</div>
                            <div className="text-gray-900 font-medium">{tech.maturity}</div>
                          </div>
                          <div>
                            <div className="text-gray-600 text-sm">Investment Required</div>
                            <div className="text-gray-900 font-medium">{tech.investmentRequired}</div>
                          </div>
                          <div>
                            <div className="text-gray-600 text-sm">Risk Level</div>
                            <div className="text-gray-900 font-medium">{tech.riskLevel}</div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <h4 className="text-gray-900 font-semibold mb-2">Skyflow Opportunity</h4>
                          <p className="text-emerald-600">{tech.skyflowOpportunity}</p>
                        </div>
                        
                        <div>
                          <div className="text-gray-600 text-sm mb-1">Key Players</div>
                          <div className="flex flex-wrap gap-2">
                            {tech.keyPlayers.map((player, i) => (
                              <span key={i} className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded-lg text-xs">
                                {player}
                              </span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>

      {/* Paper Detail Modal */}
      <AnimatePresence>
        {selectedPaper && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedPaper(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-4xl max-h-[80vh] overflow-y-auto border border-slate-200/50 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedPaper.title}</h2>
                <button
                  onClick={() => setSelectedPaper(null)}
                  className="text-gray-500 hover:text-gray-900 transition-colors text-xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-600 text-sm">Methodology</div>
                    <div className="text-gray-900">{selectedPaper.methodology}</div>
                  </div>
                  <div>
                    <div className="text-gray-600 text-sm">Impact Factor</div>
                    <div className="text-purple-600 font-bold">{selectedPaper.impactFactor}</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-gray-900 font-semibold mb-3">Key Findings</h3>
                  <div className="space-y-2">
                    {selectedPaper.keyFindings.map((finding, i) => (
                      <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Star className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{finding}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-gray-900 font-semibold mb-3">Future Applications</h3>
                  <div className="space-y-2">
                    {selectedPaper.futureApplications.map((app, i) => (
                      <div key={i} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                        <Zap className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{app}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-gray-900 font-semibold mb-3">Limitations</h3>
                  <div className="space-y-2">
                    {selectedPaper.limitations.map((limitation, i) => (
                      <div key={i} className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                        <ExternalLink className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{limitation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DeepResearchPhase;